import { Box, SxProps, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  colorLandingBrand,
  colorLandingBrandLine,
  colorLandingBrandSoft,
  colorLandingBrandStrong,
  colorLandingInk,
  colorLandingMuted,
  colorLandingSurface,
  shadowLandingRaised,
} from "src/design/tokens";
import { SubscriptionTier } from "src/data/landing/queries/subscriptionTiers";
import { tierPerkKeys } from "src/business/subscription/tierPerks";
import { tierTitleKey } from "src/business/subscription/tierLabel";
import { formatPrice } from "src/Utils/number";
import { FeatureCheckList } from "./FeatureCheckList";
import { LandingButton } from "./LandingButton";

interface PricingPlanCardProps {
  tier: SubscriptionTier;
  onSelect: () => void;
  sx?: SxProps;
}

/**
 * Marketing card for one subscription module. Every line comes from the
 * catalog: the two prices, and the perk sentences the backend nominates by
 * translation key — so re-selling a perk never touches this file.
 */
export const PricingPlanCard = ({ tier, onSelect, sx }: PricingPlanCardProps) => {
  const { t, i18n } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: colorLandingSurface,
        border: `1.5px solid ${colorLandingBrand}`,
        borderRadius: "22px",
        boxShadow: shadowLandingRaised,
        p: { xs: 3, md: 4.25 },
        ...sx,
      }}
    >
      <Typography
        component="h3"
        sx={{
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: "-0.01em",
          color: colorLandingInk,
        }}
      >
        {/* A tier the catalog no longer sells reads as its raw name rather
            than as a raw translation key. */}
        {t(tierTitleKey(tier.name), { defaultValue: tier.name })}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 1.75 }}>
        <Typography
          component="span"
          sx={{
            fontWeight: 800,
            fontSize: 46,
            lineHeight: 1,
            letterSpacing: "-0.02em",
            color: colorLandingInk,
          }}
        >
          {formatPrice(tier.monthlyPrice, i18n.language)}
        </Typography>
        <Typography component="span" sx={{ color: colorLandingMuted, fontSize: 16 }}>
          {t("landingPage.subscriptionTiers.priceSuffix")}
        </Typography>
      </Box>

      {/* The discounted per-month rate, not the yearly total: it is the number
          that compares with the monthly price printed above it. */}
      <Typography sx={{ color: colorLandingMuted, fontSize: 13, mt: 0.75 }}>
        {t("landingPage.subscriptionTiers.annualRate", {
          amount: formatPrice(tier.annualMonthlyPrice, i18n.language),
        })}
      </Typography>

      <Typography
        sx={{
          mt: 1,
          alignSelf: "flex-start",
          fontSize: 13,
          fontWeight: 700,
          color: colorLandingBrandStrong,
          backgroundColor: colorLandingBrandSoft,
          border: `1px solid ${colorLandingBrandLine}`,
          borderRadius: "8px",
          px: 1.25,
          py: 0.75,
        }}
      >
        {t("landingPage.pricing.firstMonthFree")}
      </Typography>

      <Typography
        sx={{ fontWeight: 700, color: colorLandingInk, fontSize: 15, mt: 2, mb: 0.75 }}
      >
        {t(`landingPage.subscriptionTiers.tiers.${tier.name}.description`, {
          defaultValue: "",
        })}
      </Typography>

      <FeatureCheckList
        sx={{ mb: 3.25 }}
        fontSize={15}
        items={tierPerkKeys(tier).map((perkKey) => t(perkKey))}
      />

      <LandingButton
        label={t("landingPage.pricing.cta")}
        onClick={onSelect}
        fullWidth
        sx={{ mt: "auto" }}
      />
    </Box>
  );
};
